import { OrderModel } from '@/order/models/OrderModel'

export interface IOrderChannelTotal {
    key: string
    channel: any
    count: number
    value: number
    // Fração (0..1) do total vendido.
    share: number
}

export interface IOrderListTotals {
    count: number
    validCount: number
    gross: number
    discount: number
    average: number
    canceledCount: number
    canceledValue: number
    byChannel: IOrderChannelTotal[]
}

// Soma em centavos para não acumular erro de ponto flutuante ao longo de centenas de pedidos.
function toCents(n:number):number {
    return Math.round((n || 0) * 100)
}

/**
 * Resume os pedidos carregados na listagem. Cancelados ficam fora do vendido, do
 * ticket médio e da divisão por canal: entram apenas na contagem e no valor próprios.
 *
 * Os valores são sem frete (ver OrderModel.getListValue).
 */
export function summarizeOrders(orders:OrderModel[]):IOrderListTotals {
    let grossCents = 0
    let discountCents = 0
    let canceledCents = 0
    let validCount = 0
    let canceledCount = 0
    const channels = new Map<string, {channel:any, count:number, cents:number}>()

    for (const order of orders || []) {
        const cents = toCents(order.getListValue())

        if (order.isCanceled()) {
            canceledCount++
            canceledCents += cents
            continue
        }

        validCount++
        grossCents += cents
        discountCents += toCents(order.discountValue || 0)

        const channel = order.channel || {}
        const key = String(channel.id ?? channel.enumChannel ?? channel.name ?? '-')
        const entry = channels.get(key) || {channel, count: 0, cents: 0}
        entry.count++
        entry.cents += cents
        channels.set(key, entry)
    }

    const byChannel = Array.from(channels.entries())
        .map(([key, e]) => ({
            key,
            channel: e.channel,
            count: e.count,
            value: e.cents / 100,
            share: grossCents > 0 ? e.cents / grossCents : 0,
        }))
        .sort((a, b) => b.value - a.value)

    return {
        count: (orders || []).length,
        validCount,
        gross: grossCents / 100,
        discount: discountCents / 100,
        average: validCount ? Math.round(grossCents / validCount) / 100 : 0,
        canceledCount,
        canceledValue: canceledCents / 100,
        byChannel,
    }
}
